<template>
  <div :class="timelineClass">
    <div :id="chartId" class="container-chart"></div>
  </div>
</template>

<script>
import moment from 'moment'
import * as d3 from 'd3'
import { utilsMixin, timeMixin } from '../../utils/mixins'

export default {
  name: 'SVGTimeline',
  mixins: [utilsMixin, timeMixin],
  props: ['chartId', 'start', 'end'],
  data() {
    return {
      config: {
        format: 'DD/MM/YYYY',
        useCurrent: false,
        locale: this.$locale,
      },
      margin: { top: 25, right: 20, bottom: 30, left: 50 },
      width: 1080,
      height: 40,
      svg: null,
      chart: Object(),
      area: Object(),
      xAxis: Object(),
      yAxis: Object(),
      xScale: null,
      x0: null,
      yAxisScale: null,
      timelineX: null,
      brush: null,
      brushRange: null,
      scale: null,
      currentScale: 2.7,
      currentXOffset: -1900,
      zoom: null,
      savedStart: null,
      savedEnd: null,
      dontEmitUpdate: false,
    }
  },
  mounted() {
    window.addEventListener('resize', this.handleTimelineResize)
    this.loadTimeline()
    this.redrawBrushPeriod()
  },
  computed: {
    timelineClass: function() {
      return {
        'col-sm-9': this.isLargeScreen || this.isVeryLargeScreen,
        hidden: this.isSmallScreen || this.isMediumScreen,
        'my-auto': true,
      }
    },
  },
  watch: {
    start: {
      handler: function() {
        this.redrawBrushPeriod()
      },
    },
    end: {
      handler: function() {
        this.redrawBrushPeriod()
      },
    },
  },
  methods: {
    handleTimelineResize(event) {
      this.handleResize(event)

      if ((this.isLargeScreen || this.isVeryLargeScreen) && !this.svg) {
        this.loadTimeline()
      } else if (this.isSmallScreen || this.isSmartphoneDevice) {
        this.svg = undefined
      }
    },
    loadTimeline() {
      let that = this
      let timelineEnd = moment(this.end).isValid() ? moment(this.end) : moment()
      let timelineStart = moment(this.end).subtract(1, 'year')

      //Scale range
      this.x0 = d3
        .scaleTime()
        .range([0, this.width])
        .domain([timelineStart, timelineEnd])
      d3.scaleLinear().range([this.height, 0])
      this.xScale = this.x0.copy()

      //When user zoom in the timeline
      this.zoom = d3
        .zoom()
        .scaleExtent([1, 10])
        .on('zoom', that.onZoom)

      let svgWidth = this.width
      let svgHeight = this.height + this.margin.top

      //Main svg used to display the timeline
      this.svg = d3
        .select('#' + this.chartId)
        .append('svg')
        .attr('width', '100%')
        .attr('viewBox', '0 0 ' + svgWidth + ' ' + svgHeight)
        .attr('preserveAspectRatio', 'xMinYMin')
        .append('g')
        .attr('transform', 'translate(0,' + this.margin.top + ')')
        .call(this.zoom)

      //Adding transparent rect to allow timeline grabbing
      this.svg
        .append('rect')
        .attr('width', '100%')
        .attr('height', this.svgHeight)
        .attr('fill', 'transparent')
        .attr('y', -30)
        .attr('style', 'cursor: grab;')
        .on('mousedown', function() {
          d3.selectAll('.tick')
            .selectAll('rect')
            .remove()
          d3.select(this).attr('style', 'cursor: grabbing;')
        })
        .on('mouseup', function() {
          d3.select(this).attr('style', 'cursor: grab;')
        })
        .on('mousemove', function() {
          d3.select(this).attr('style', 'cursor: grab;')
        })

      //Brush use to select a period
      this.brush = d3
        .brushX()
        //.extent([[0, 0], [width, height2]])
        .extent([
          [this.xScale.range()[0], 0],
          [this.xScale.range()[1], this.height],
        ])
        .on('brush end', function() {
          that.scale = d3.event.selection || that.xScale.range()

          //Round x value according to the selected period
          let d0 = that.scale.map(that.xScale.invert)
          let d1 = d0.map(d3.timeDay.floor)

          that.$log.debug('brush scaling dates', d1)

          if (moment(d1[0]).isAfter(moment(d1[1]))) {
            d1[0] = d3.timeDay.floor(d0[0])
            d1[1] = d3.timeDay.offset(d1[0])
          } else if (moment(d1[0]).isSame(d1[1])) {
            d1[1] = moment(d1[0])
              .add(1, 'day')
              .toDate()
          }

          // If it is the same period : ignore the brush event
          if (
            that.savedStart === moment(d1[0]).format(that.dateFormat) &&
            that.savedEnd === moment(d1[1]).format(that.dateFormat)
          ) {
            return
          }

          if (d3.event.type === 'end') {
            that.savedStart = moment(d1[0]).format(that.dateFormat)
            that.savedEnd = moment(d1[1]).format(that.dateFormat)

            that.$log.debug('Brush end event', that.savedStart, that.savedEnd)

            d3.select(this)
              .transition()
              .call(d3.event.target.move, d1.map(that.xScale))
          }

          that.brushRange = [that.xScale(d1[0]), that.xScale(d1[1])]

          if (
            !(
              that.savedStart != moment(d1[0]).format(that.dateFormat) &&
              that.savedEnd != moment(d1[1]).format(that.dateFormat) &&
              d3.event.sourceEvent
            )
          ) {
            that.emitNewDateRange(d3.event.type, moment(d1[0]), moment(d1[1]))
          }
        })

      this.svg
        .append('g')
        .attr('class', 'brush')
        .call(this.brush)
        .call(
          this.brush.move,
          this.start != undefined && this.end != undefined && that.xScale != undefined
            ? [that.xScale(this.start), that.xScale(this.end)]
            : this.xScale.range(),
        )

      //Adding x Axis
      this.xAxis = d3
        .axisTop()
        .scale(this.xScale)
        .ticks(d3.timeDay)

      this.svg
        .append('g')
        .attr('class', 'axis axis--x')
        .attr('transform', 'translate(0)')
        .call(this.xAxis)

      //Text and size displayed according to the zoom level and the date
      this.displayTickLine()

      //We init the initial zoom level to display a 4 months period
      this.svg.call(this.zoom.transform, function() {
        return d3.zoomIdentity.translate(that.currentXOffset, 0).scale(that.currentScale)
      })

      this.svg
        .select('.brush')
        .call(this.brush.move, [this.xScale(this.start), this.xScale(this.end)])
    },
    redrawBrushPeriod: function() {
      if (this.end.isAfter(this.xScale.domain()[1]) || this.end.isBefore(this.xScale.domain()[0])) {
        let that = this
        this.svg.call(this.zoom.transform, function() {
          let xOffset = -that.xScale(that.end) + (that.currentXOffset + that.brushRange[1])
          return d3.zoomIdentity.translate(xOffset, 0).scale(that.currentScale)
        })
      }
      this.svg
        .select('.brush')
        .call(this.brush.move, [this.xScale(this.start), this.xScale(this.end)])
    },
    displayTickLine: function(transformScale) {
      let that = this
      //Text and size displayed according to the zoom level and the date
      this.svg
        .selectAll('.axis--x line')
        .attr('y2', function(d) {
          let day = new Date(d).getDate()
          if (day == 1) {
            return 33
          } else {
            return 22
          }
        })
        .attr('stroke', function(d) {
          let day = new Date(d).getDay()
          if (day == 6 || day == 0) {
            //return "#f43b47";
            return '#dfa579'
            /*return "#e49375";*/
          } else {
            return '#feffe9'
          }
        })
        .attr('stroke-width', '4px')
        .on('click', function(d) {
          that.$log.debug('Click on a specific day:', d)
          let day = new Date(d).getDay()
          if (day == 6 || day == 0) {
            if (day == 6) {
              that.svg
                .select('.brush')
                .call(that.brush.move, [
                  that.xScale(new Date(d)),
                  that.xScale(new Date(moment(d).add(1, 'days'))),
                ])
            } else if (day == 0) {
              that.svg
                .select('.brush')
                .call(that.brush.move, [
                  that.xScale(new Date(moment(d).add(-1, 'days'))),
                  that.xScale(new Date(d)),
                ])
            }
          }
        })

      //Set text values (month and day)
      this.svg
        .selectAll('text')
        .attr('y', function(d) {
          let day = new Date(d).getDate()
          if (day == 15) {
            return -14
          } else {
            return -4
          }
        })
        .text(function(d) {
          let day = new Date(d).getDate()
          if (day == 15) {
            return that.getMonthString(d)
          } else {
            return transformScale != undefined && transformScale.k > 5 ? moment(d).format('DD') : ''
          }
        })
        .attr('font-size', function(d) {
          let day = new Date(d).getDate()
          if (day == 15) {
            return 'small'
          } else {
            return 'x-small'
          }
        })
        .on('click', function(d) {
          that.handleMonthClick(d, this)
        })
    },
    onZoom: function(scale) {
      let that = this

      if (d3.event.sourceEvent && d3.event.sourceEvent.type === 'brush') return // ignore zoom-by-brush

      that.selectedPreset = 'custom'

      d3.selectAll('.tick')
        .selectAll('rect')
        .remove()

      let transformScale = scale ? scale : d3.event.transform
      this.currentXOffset = transformScale.x

      this.xScale.domain(transformScale.rescaleX(this.x0).domain())

      this.svg.select('.brush').call(this.brush)
      this.svg.select('.axis--x').call(this.xAxis)

      let d0 = this.brushRange.map(this.xScale.invert)
      let d1 = d0.map(d3.timeDay.round)

      if (d1[0] >= d1[1]) {
        d1[0] = d3.timeDay.floor(d0[0])
        d1[1] = d3.timeDay.offset(d1[0])
      }

      this.brushRange = [this.xScale(d1[0]), this.xScale(d1[1])]

      this.svg.select('.brush').call(this.brush.move, this.brushRange)

      this.savedStart = moment(d1[0]).format(this.dateFormat)
      this.savedEnd = moment(d1[1]).format(this.dateFormat)

      if (!this.isJustMounted) {
        this.emitNewDateRange(d1[0], d1[1])
      } else {
        this.isJustMounted = false
      }

      this.currentScale = transformScale.k

      //Add tick lines
      this.displayTickLine(transformScale)
    },
    handleMonthClick: function(dateElement, tickElement) {
      let that = this
      let date = moment(dateElement)

      d3.selectAll('.axis--x text').attr('class', null)
      d3.select(tickElement).attr('class', 'selected')

      let startDateFormatted = moment(date)
        .startOf('month')
        .format(this.dateFormat)
      let endDateFormatted = moment(date)
        .endOf('month')
        .format(this.dateFormat)

      const startDate = moment(startDateFormatted, this.dateFormat).toDate()
      const endDate = moment(endDateFormatted, this.dateFormat).toDate()

      this.svg
        .select('.brush')
        .call(this.brush.move, [that.xScale(startDate), that.xScale(endDate)])

      this.svg.selectAll('.axis--x text').each(function() {
        let tick = d3.select(d3.select(this).node().parentNode)
        let text = tick.select('text'),
          bBox = text.node().getBBox()
        // Adding rect behind the selected month
        if (text.attr('class') == 'selected') {
          tick
            .insert('rect', ':first-child')
            .attr('x', bBox.x - 8)
            .attr('y', bBox.y - 8)
            .attr('rx', 2)
            .attr('ry', 2)
            .attr('width', bBox.width + 14)
            .attr('height', bBox.height + 10)
            .style('fill', '#20343D')
        } else {
          tick.selectAll('rect').remove()
        }
      })
    },
    emitNewDateRange: async function(eventType, start, end) {
      const dateRange = { start, end }

      if (eventType === 'brush') {
        this.$emit('update', dateRange)
      }
      if (eventType === 'end') {
        this.$emit('validate', dateRange)
      }
    },
    getMonthString: function(d) {
      let month = moment(d)
      moment.locale(this.$locale)
      let monthString = month.format('MMM YYYY')
      return monthString.charAt(0).toUpperCase() + monthString.slice(1)
    },
  },
}
</script>
